import { fail } from './errors.js';

/*
 * 월 결산: 결산 회의록(센터 엑셀 양식) · 마감 체크 · 지급 검토 계산.
 * 금액은 모두 정수 원. 현금 칸만 환불을 빼서 적을 수 있어 음수를 허용한다. 계산은 이 파일의 함수에서만 하고 화면은 결과만 보여 준다.
 * 업무 규칙: docs/CLOSING_MINUTES_RULES.md
 */

/** 주차 칸 최대 수(달력에 따라 4~6주) */
export const MINUTES_WEEKS = 6;
export const INCOME_COLS = ['CASH', 'TRANSFER', 'CARD', 'SPECIAL_EDU', 'DEV_REHAB', 'COMMUNITY'] as const;
export type IncomeCol = (typeof INCOME_COLS)[number];
export const INCOME_COL_LABEL: Record<IncomeCol, string> = {
  CASH: '현금', TRANSFER: '계좌이체', CARD: '카드', SPECIAL_EDU: '1특수교육', DEV_REHAB: '2발달재활', COMMUNITY: '3지역사회',
};

export const STAFF_AREAS = ['심리평가', '상담', '언어', '놀이', '감각통합'] as const;
export type StaffArea = (typeof STAFF_AREAS)[number];
/** 전체 수업수를 넣으면 일반수업을 자동 계산하는 영역 */
export const TOTAL_SESSION_AREAS: readonly StaffArea[] = ['언어', '놀이', '감각통합'];
const DEFAULT_STAFF: ReadonlyArray<readonly [string, StaffArea]> = [
  ['심리1', '심리평가'], ['상담1', '상담'], ['상담2', '상담'], ['상담3', '상담'], ['언어1', '언어'], ['언어2', '언어'],
  ['놀이1', '놀이'], ['놀이2', '놀이'], ['감통1', '감각통합'], ['감통2', '감각통합'], ['감통3', '감각통합'],
];

export const PERF_EVAL_ROWS = [
  ['PSY_ASSESS', '종합심리평가검사'], ['DEV_ASSESS', '발달평가'], ['DEV_ASSESS_BRIEF', '발달평가(간편)'], ['CAT', 'CAT'],
  ['COUNSEL', '일반상담'], ['NOSHOW', '노쇼/당일취소'], ['LANG_LEARN', '언어+학습'], ['LANG_ASSESS', '언어평가'],
  ['LEARN_ASSESS', '학습평가'], ['PRAGMATIC', '화용언어검사'],
] as const;
export const PERF_CLASS_ROWS = [
  ['SPECIAL_EDU', '특수교육'], ['DEV_REHAB', '발달재활'], ['MIND_INVEST', '마음투자/실비'], ['WELFARE', '복지관'],
  ['GENERAL', '일반수업'], ['COMMUNITY', '지역사회'],
] as const;

/** ops = (생활비, 예비비 외) 지출에 포함, reserve = 적립 성격(순이익에 다시 더함), annex = 별관 항목(별관외지출에서 뺌) */
const EXPENSE_DEFAULTS: ReadonlyArray<readonly [string, string, 'ops' | 'reserve' | 'annex' | null]> = [
  ['PAYROLL', '인건비', 'ops'], ['RENT', '임대료관리비', 'ops'], ['INSURANCE4', '4대보험', null], ['TAX', '세금', 'ops'], ['FIRE_INS', '화재보험', 'ops'],
  ['OFFICE', '통신/사무비', 'ops'], ['YELLOW_UMBRELLA', '노란우산', 'reserve'], ['LIVING', '생활비', null], ['SAVINGS', '예적금', 'reserve'],
  ['TAX_SERVICE', '세무비', 'ops'], ['OTHER', '기타비', 'ops'], ['SEVERANCE', '퇴직금', 'ops'], ['ANNEX', '', 'annex'],
];

export const MINUTES_STAFF = { min: 1, max: 20 } as const;
export const MINUTES_ROWS_MAX = 40;
export const MINUTES_TEXT_MAX = 2000;
/** 칸 하나 상한(10억 원) — 입력 실수 방지 */
export const MINUTES_CELL_MAX = 1_000_000_000;
export const MINUTES_HISTORY_MAX = 300;

export interface MinutesWeek {
  id: string;
  start: string; end: string;           // YYYY-MM-DD
  amounts: Record<IncomeCol, number>;
  note: string;
  expense: number;                      // 주간 지출
}
export interface MinutesStaff {
  id: string;
  code: string;                         // 심리1, 언어2 …
  name: string;                         // 실명은 앱 데이터로만
  area: StaffArea | '';
  addGuarantee: number;                 // 보장분 추가 건수
  totalSessions: number | null;         // 비어 있으면 일반수업을 직접 입력
}
export interface MinutesPerfRow {
  key: string;
  label: string;
  group: 'EVAL' | 'CLASS';
  counts: Record<string, number>;       // 담당자 id → 건수
}
export interface MinutesExpense {
  key: string;
  label: string;
  amount: number;
  ops: boolean; reserve: boolean; annex: boolean;
}

export interface MeetingMinutes {
  title: string;         // 비어 있으면 자동 제목
  period: string;        // 비어 있으면 자동 결산 기간
  centerName: string;
  meetingDate: string;   // YYYY-MM-DD 또는 ''
  attendees: string;
  weeks: MinutesWeek[];
  staff: MinutesStaff[];
  perfRows: MinutesPerfRow[];
  expenses: MinutesExpense[];
  /** 자동 계산 칸을 사용자가 덮어쓴 값. 없으면 계산값을 쓴다. */
  overrides: Record<string, number>;
  agenda: string;        // 상의할 내용 및 필요물품
  referral: string;      // 타기관 의뢰 치료비 및 청구 내용
}

const pad = (n: number) => String(n).padStart(2, '0');
const lastDay = (month: string) => {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
};

/** 그 달을 일~토로 끊은 주차. 첫 주는 1일부터, 마지막 주는 말일까지. */
export function monthWeeks(month: string): Array<{ start: string; end: string }> {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = lastDay(month);
  const out: Array<{ start: string; end: string }> = [];
  for (let from = 1; from <= last;) {
    const to = Math.min(last, from + 6 - new Date(Date.UTC(y, m - 1, from)).getUTCDay());
    out.push({ start: `${month}-${pad(from)}`, end: `${month}-${pad(to)}` });
    from = to + 1;
  }
  return out;
}

const dayLabel = (iso: string) => `${iso.slice(5, 7)}월${iso.slice(8, 10)}일`;
/** 표시 형식: 10월04일-10월10일 */
export const weekLabel = (start: string, end: string) => `${dayLabel(start)}-${dayLabel(end)}`;
export const minutesPeriod = (month: string) => weekLabel(`${month}-01`, `${month}-${pad(lastDay(month))}`);
export const minutesTitle = (centerName: string, month: string) => `${centerName} ${month.slice(0, 4)}년도 ${Number(month.slice(5, 7))}월 결산 회의록`.trim();
export function prevMonth(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return m === 1 ? `${y - 1}-12` : `${y}-${pad(m - 1)}`;
}

/** 아직 쓰지 않은 id(행·열·주차 추가용) */
export function nextId(prefix: string, used: readonly string[]): string {
  let i = 1;
  while (used.includes(`${prefix}${i}`)) i += 1;
  return `${prefix}${i}`;
}

const zeroAmounts = () => Object.fromEntries(INCOME_COLS.map((c) => [c, 0])) as Record<IncomeCol, number>;
export const blankWeek = (id: string, start: string, end: string): MinutesWeek => ({ id, start, end, amounts: zeroAmounts(), note: '', expense: 0 });
export const blankStaff = (id: string, code: string, area: StaffArea | '' = ''): MinutesStaff => ({ id, code, name: '', area, addGuarantee: 0, totalSessions: null });
const zeroCounts = (staff: readonly MinutesStaff[]) => Object.fromEntries(staff.map((s) => [s.id, 0]));
const defaultPerfRows = (staff: readonly MinutesStaff[]): MinutesPerfRow[] => [
  ...PERF_EVAL_ROWS.map(([key, label]) => ({ key, label, group: 'EVAL' as const, counts: zeroCounts(staff) })),
  ...PERF_CLASS_ROWS.map(([key, label]) => ({ key, label, group: 'CLASS' as const, counts: zeroCounts(staff) })),
];
const defaultExpenses = (): MinutesExpense[] => EXPENSE_DEFAULTS.map(([key, label, kind]) => ({
  key, label, amount: 0, ops: kind === 'ops', reserve: kind === 'reserve', annex: kind === 'annex',
}));

export function emptyMinutes(month: string, centerName = ''): MeetingMinutes {
  const staff = DEFAULT_STAFF.map(([code, area], i) => blankStaff(`s${i + 1}`, code, area));
  return {
    title: '', period: '', centerName, meetingDate: '', attendees: '',
    weeks: monthWeeks(month).map((w, i) => blankWeek(`w${i + 1}`, w.start, w.end)),
    staff, perfRows: defaultPerfRows(staff), expenses: defaultExpenses(), overrides: {},
    agenda: '', referral: '',
  };
}

/** 전월 복사: 치료사 명단·항목·지출 고정값은 이어받고, 수입·건수·메모는 비운다. */
export function carryOverMinutes(prev: MeetingMinutes, month: string): MeetingMinutes {
  const staff = prev.staff.map((s) => ({ ...s, addGuarantee: 0, totalSessions: null }));
  return {
    ...emptyMinutes(month, prev.centerName),
    staff,
    perfRows: prev.perfRows.map((r) => ({ ...r, counts: zeroCounts(staff) })),
    expenses: prev.expenses.map((e) => ({ ...e })),
  };
}

/** 정수 원(0 이상, 상한)으로. 빈 칸·잘못된 값은 0. */
export function cellWon(v: unknown): number {
  const n = signedWon(v);
  return n <= 0 ? 0 : n;
}

/** 현금 칸용: 환불을 빼서 음수가 될 수 있다. */
export function signedWon(v: unknown): number {
  const n = typeof v === 'string' ? Number(v.replace(/[,\s원]/g, '')) : Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(-MINUTES_CELL_MAX, Math.min(MINUTES_CELL_MAX, Math.trunc(n)));
}

const sum = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);

export const generalIsAuto = (s: MinutesStaff) => s.totalSessions !== null && (TOTAL_SESSION_AREAS as readonly string[]).includes(s.area);

export interface MinutesTotals {
  /** 칸 키 → 계산값(덮어쓰기 전). 아래 값들은 덮어쓴 값을 반영한 결과다. */
  auto: Record<string, number>;
  weekIncome: Record<string, number>;      // 주 id → 주 합계
  incomeByCol: Record<IncomeCol, number>;  // 열별 수입 합계
  totalIncome: number;
  weeklyExpenseTotal: number;
  general: Record<string, number>;         // 담당자 id → 자동 계산한 일반수업
  evalSubtotal: Record<string, number>;    // 담당자별 평가·상담 소계
  classSubtotal: Record<string, number>;   // 담당자별 수업 소계
  staffFinal: Record<string, number>;      // 담당자별 Final
  perfRowTotal: Record<string, number>;    // 행 합계
  evalTotal: number; classTotal: number; guaranteeTotal: number; finalTotal: number;
  expenseTotal: number;                    // 총지출
  opsExpense: number;                      // (생활비, 예비비 외) 지출
  opsRatio: number;                        // 위 금액 ÷ 총지출(%)
  baseExpense: number;                     // 별관외지출 = 총지출 − 별관 항목
  expenseRatio: Record<string, number>;    // 항목 ÷ 별관외지출, 생활비만 ÷ 총지출(%, 소수 2자리)
  netProfit: number;                       // 순이익 = 총수입 − 별관외지출 + 적립 항목
  cashNet: number;                         // 총수입 − 주간 지출 합계
  /** 주차별 지출 − 항목별 지출. 0 이면 일치 */
  expenseGap: number;
}

export function minutesTotals(m: MeetingMinutes): MinutesTotals {
  const auto: Record<string, number> = {};
  // 덮어쓴 값은 엑셀처럼 그다음 계산에도 쓰인다
  const cell = (key: string, computed: number) => { auto[key] = computed; return m.overrides[key] ?? computed; };
  const each = <T>(xs: readonly T[], id: (x: T) => string, f: (x: T) => number) => Object.fromEntries(xs.map((x) => [id(x), f(x)]));

  const weekIncome = each(m.weeks, (w) => w.id, (w) => cell(`weekTotal:${w.id}`, sum(INCOME_COLS.map((c) => w.amounts[c]))));
  const incomeByCol = each(INCOME_COLS, (c) => c, (c) => cell(`incomeCol:${c}`, sum(m.weeks.map((w) => w.amounts[c])))) as Record<IncomeCol, number>;
  const totalIncome = cell('totalIncome', sum(Object.values(weekIncome)));
  const weeklyExpenseTotal = cell('weeklyExpenseTotal', sum(m.weeks.map((w) => w.expense)));

  const rowCount = (key: string, id: string) => m.perfRows.find((r) => r.key === key)?.counts[id] ?? 0;
  const general = each(m.staff.filter(generalIsAuto), (s) => s.id, (s) => cell(`general:${s.id}`, s.totalSessions! - rowCount('SPECIAL_EDU', s.id) - rowCount('DEV_REHAB', s.id)));
  const countOf = (r: MinutesPerfRow, id: string) => (r.key === 'GENERAL' ? general[id] : undefined) ?? r.counts[id] ?? 0;
  const groupSum = (group: MinutesPerfRow['group'], id: string) => sum(m.perfRows.filter((r) => r.group === group).map((r) => countOf(r, id)));
  const evalSubtotal = each(m.staff, (s) => s.id, (s) => cell(`evalSub:${s.id}`, groupSum('EVAL', s.id)));
  const classSubtotal = each(m.staff, (s) => s.id, (s) => cell(`classSub:${s.id}`, groupSum('CLASS', s.id)));
  const staffFinal = each(m.staff, (s) => s.id, (s) => cell(`final:${s.id}`, classSubtotal[s.id]! + s.addGuarantee + evalSubtotal[s.id]!));
  const perfRowTotal = each(m.perfRows, (r) => r.key, (r) => cell(`rowTotal:${r.key}`, sum(m.staff.map((s) => countOf(r, s.id)))));

  const expenseTotal = cell('expenseTotal', sum(m.expenses.map((e) => e.amount)));
  const flagged = (f: 'ops' | 'reserve' | 'annex') => sum(m.expenses.filter((e) => e[f]).map((e) => e.amount));
  const opsExpense = cell('opsExpense', flagged('ops'));
  const baseExpense = cell('baseExpense', expenseTotal - flagged('annex'));
  return {
    auto, weekIncome, incomeByCol, totalIncome, weeklyExpenseTotal, general, evalSubtotal, classSubtotal, staffFinal, perfRowTotal,
    evalTotal: cell('evalTotal', sum(Object.values(evalSubtotal))),
    classTotal: cell('classTotal', sum(Object.values(classSubtotal))),
    guaranteeTotal: cell('guaranteeTotal', sum(m.staff.map((s) => s.addGuarantee))),
    finalTotal: cell('finalTotal', sum(Object.values(staffFinal))),
    expenseTotal, opsExpense, baseExpense,
    opsRatio: cell('opsRatio', ratio(opsExpense, expenseTotal)),
    // 생활비만 총지출로 나눈다(센터 확인 2026-11-02)
    expenseRatio: each(m.expenses, (e) => e.key, (e) => cell(`ratio:${e.key}`, ratio(e.amount, e.key === 'LIVING' ? expenseTotal : baseExpense))),
    netProfit: cell('netProfit', totalIncome - baseExpense + flagged('reserve')),
    cashNet: cell('cashNet', totalIncome - weeklyExpenseTotal),
    expenseGap: weeklyExpenseTotal - expenseTotal,
  };
}

/** 비율(%)을 소수 2자리로. 분모가 0 이면 0. */
export const ratio = (part: number, whole: number) => (whole ? Math.round((part / whole) * 10000) / 100 : 0);

/** 없어진 행·열을 가리키는 덮어쓰기 값을 지운다(같은 id 를 다시 쓸 때 되살아나지 않게). */
export function pruneOverrides(m: MeetingMinutes): MeetingMinutes {
  const known = minutesTotals({ ...m, overrides: {} }).auto;
  return { ...m, overrides: Object.fromEntries(Object.entries(m.overrides).filter(([k]) => k in known)) };
}

/** 예전 저장 형식(항목 고정, 7일 단위 주차)을 새 형식으로. 주차는 금액의 뜻이 바뀌지 않게 저장 당시 기간을 그대로 둔다. */
function upgradeLegacy(r: Record<string, unknown>, month: string): Record<string, unknown> {
  const names = Array.isArray(r.staffNames) ? (r.staffNames as unknown[]) : [];
  const list = (v: unknown) => (Array.isArray(v) ? (v as unknown[]) : []);
  const obj = (v: unknown) => (v && typeof v === 'object' ? (v as Record<string, unknown>) : {});
  const staff = names.map((code, i) => ({ id: `s${i + 1}`, code, name: '', area: '', addGuarantee: list(r.addGuarantee)[i], totalSessions: null }));
  const weeks = [];
  for (let i = 0; i * 7 < lastDay(month); i += 1) {
    const w = obj(list(r.income)[i]);
    weeks.push({
      id: `w${i + 1}`, start: `${month}-${pad(i * 7 + 1)}`, end: `${month}-${pad(Math.min(i * 7 + 7, lastDay(month)))}`,
      amounts: w, note: w.note, expense: list(r.weeklyExpense)[i],
    });
  }
  return {
    centerName: r.centerName, meetingDate: r.meetingDate, attendees: r.attendees, agenda: r.agenda, referral: r.referral, weeks, staff,
    perfRows: defaultPerfRows([]).map((row) => ({ ...row, counts: Object.fromEntries(staff.map((s, i) => [s.id, list(obj(r.perf)[row.key])[i]])) })),
    expenses: defaultExpenses().map((e) => ({ ...e, amount: obj(r.expenses)[e.key] })),
  };
}

/** 저장 전 정리: 예전 형식을 올리고, 모양을 맞추고, 금액은 정수 원으로, 글자 수 제한. */
export function normalizeMinutes(raw: unknown, month: string): MeetingMinutes {
  const given = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const r = Array.isArray(given.staff) ? given : upgradeLegacy(given, month);
  const str = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : '');
  const rows = (v: unknown) => (Array.isArray(v) ? v : []).filter((x) => x && typeof x === 'object') as Array<Record<string, unknown>>;
  const uniqueIds = (ids: unknown[], prefix: string) => {
    const used: string[] = [];
    return ids.map((g) => {
      const id = typeof g === 'string' && /^[\w-]{1,24}$/.test(g) && !used.includes(g) ? g : nextId(prefix, [...used, ...ids.map(String)]);
      used.push(id);
      return id;
    });
  };

  const staffIn = rows(r.staff).slice(0, MINUTES_STAFF.max);
  if (staffIn.length < MINUTES_STAFF.min) fail('VALIDATION', '담당자는 1명 이상이어야 합니다.');
  const staffIds = uniqueIds(staffIn.map((s) => s.id), 's');
  const staff = staffIn.map((s, i): MinutesStaff => ({
    id: staffIds[i]!, code: str(s.code, 20).trim() || `담당자 ${i + 1}`, name: str(s.name, 20).trim(),
    area: (STAFF_AREAS as readonly unknown[]).includes(s.area) ? (s.area as StaffArea) : '',
    addGuarantee: cellWon(s.addGuarantee),
    totalSessions: s.totalSessions === null || s.totalSessions === undefined || s.totalSessions === '' ? null : cellWon(s.totalSessions),
  }));

  const weeksIn = (rows(r.weeks).length ? rows(r.weeks) : monthWeeks(month) as Array<Record<string, unknown>>).slice(0, MINUTES_WEEKS);
  const weekIds = uniqueIds(weeksIn.map((w) => w.id), 'w');
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  const weeks = weeksIn.map((w, i): MinutesWeek => {
    const start = str(w.start, 10), end = str(w.end, 10);
    if (!iso.test(start) || !iso.test(end) || start > end) fail('VALIDATION', `${i + 1}주 기간을 확인해 주세요.`);
    const a = (w.amounts && typeof w.amounts === 'object' ? w.amounts : {}) as Record<string, unknown>;
    return {
      id: weekIds[i]!, start, end, note: str(w.note, 100), expense: cellWon(w.expense),
      amounts: Object.fromEntries(INCOME_COLS.map((c) => [c, c === 'CASH' ? signedWon(a[c]) : cellWon(a[c])])) as Record<IncomeCol, number>,
    };
  });

  const perfIn = rows(r.perfRows).slice(0, MINUTES_ROWS_MAX);
  const perfKeys = uniqueIds(perfIn.map((x) => x.key), 'R');
  const perfRows = perfIn.map((x, i): MinutesPerfRow => {
    const c = (x.counts && typeof x.counts === 'object' ? x.counts : {}) as Record<string, unknown>;
    return { key: perfKeys[i]!, label: str(x.label, 30), group: x.group === 'CLASS' ? 'CLASS' : 'EVAL', counts: Object.fromEntries(staff.map((s) => [s.id, cellWon(c[s.id])])) };
  });
  const expIn = rows(r.expenses).slice(0, MINUTES_ROWS_MAX);
  const expKeys = uniqueIds(expIn.map((x) => x.key), 'E');
  const expenses = expIn.map((x, i): MinutesExpense => ({
    key: expKeys[i]!, label: str(x.label, 30), amount: cellWon(x.amount), ops: x.ops === true, reserve: x.reserve === true, annex: x.annex === true,
  }));

  const date = str(r.meetingDate, 10);
  if (date && !iso.test(date)) fail('VALIDATION', '회의일 형식은 YYYY-MM-DD 입니다.');
  const overrides = Object.fromEntries(Object.entries((r.overrides && typeof r.overrides === 'object' ? r.overrides : {}) as Record<string, unknown>)
    .filter(([, v]) => typeof v === 'number' && Math.abs(v) <= MINUTES_CELL_MAX * 1000)
    .map(([k, v]) => [k, Math.round((v as number) * 100) / 100]));
  return pruneOverrides({
    title: str(r.title, 100), period: str(r.period, 40), centerName: str(r.centerName, 60), meetingDate: date, attendees: str(r.attendees, 200),
    weeks, staff, perfRows, expenses, overrides,
    agenda: str(r.agenda, MINUTES_TEXT_MAX), referral: str(r.referral, MINUTES_TEXT_MAX),
  });
}

/* ---------------- 변경 이력 ---------------- */

/** 칸 하나의 바뀐 기록. 금액이 들어 있어 감사 로그가 아니라 결산 기록 안에 둔다. */
export interface MinutesChange { at: string; by: string; label: string; from: string; to: string }

const CALC_LABEL: Record<string, string> = {
  totalIncome: '월 총수입', weeklyExpenseTotal: '주간 지출 합계', evalTotal: '평가·상담 소계 합계', classTotal: '수업 소계 합계',
  guaranteeTotal: 'add guarantee 합계', finalTotal: 'Final 합계', expenseTotal: '총지출', opsExpense: '(생활비, 예비비 외) 지출',
  opsRatio: '(생활비, 예비비 외) 지출 비율', baseExpense: '별관외지출', netProfit: '순이익', cashNet: '총수입 − 총지출',
};
function calcLabel(m: MeetingMinutes, key: string): string {
  const [kind, id] = key.split(':') as [string, string | undefined];
  const code = m.staff.find((s) => s.id === id)?.code ?? '';
  const expense = m.expenses.find((e) => e.key === id)?.label ?? '';
  const week = m.weeks.findIndex((w) => w.id === id) + 1;
  const row = m.perfRows.find((r) => r.key === id)?.label ?? '';
  switch (kind) {
    case 'weekTotal': return `${week}주 합계`;
    case 'incomeCol': return `${INCOME_COL_LABEL[id as IncomeCol]} 합계`;
    case 'general': return `${code} 일반수업`;
    case 'evalSub': return `${code} 평가·상담 소계`;
    case 'classSub': return `${code} 수업 소계`;
    case 'final': return `${code} Final`;
    case 'rowTotal': return `${row} 합계`;
    case 'ratio': return `${expense} 비율`;
    default: return CALC_LABEL[key] ?? key;
  }
}

function flatten(m: MeetingMinutes): Map<string, { label: string; value: string }> {
  const out = new Map<string, { label: string; value: string }>();
  const put = (key: string, label: string, value: string | number | boolean | null) => out.set(key, { label, value: value === null ? '' : String(value) });
  for (const [k, l] of [['title', '제목'], ['period', '결산 기간'], ['centerName', '센터명'], ['meetingDate', '회의일'], ['attendees', '참석자'], ['agenda', '상의할 내용'], ['referral', '타기관 의뢰']] as const) put(k, l, m[k]);
  m.weeks.forEach((w, i) => {
    put(`w:${w.id}:start`, `${i + 1}주 시작일`, w.start); put(`w:${w.id}:end`, `${i + 1}주 종료일`, w.end);
    put(`w:${w.id}:note`, `${i + 1}주 비고`, w.note); put(`w:${w.id}:expense`, `${i + 1}주 지출`, w.expense);
    for (const c of INCOME_COLS) put(`w:${w.id}:${c}`, `${i + 1}주 ${INCOME_COL_LABEL[c]}`, w.amounts[c]);
  });
  for (const s of m.staff) {
    put(`s:${s.id}:code`, `${s.code} 코드`, s.code); put(`s:${s.id}:name`, `${s.code} 이름`, s.name); put(`s:${s.id}:area`, `${s.code} 영역`, s.area);
    put(`s:${s.id}:guarantee`, `${s.code} add guarantee`, s.addGuarantee); put(`s:${s.id}:total`, `${s.code} 전체 수업수`, s.totalSessions);
    for (const r of m.perfRows) put(`p:${r.key}:${s.id}`, `${s.code} ${r.label}`, r.counts[s.id] ?? 0);
  }
  for (const r of m.perfRows) put(`p:${r.key}:label`, `${r.label} 항목명`, r.label);
  for (const e of m.expenses) {
    const l = e.label || '빈 항목';
    put(`e:${e.key}:label`, `${l} 항목명`, e.label); put(`e:${e.key}:amount`, `${l} 금액`, e.amount);
    put(`e:${e.key}:ops`, `${l} 주요 지출 포함`, e.ops); put(`e:${e.key}:reserve`, `${l} 적립`, e.reserve); put(`e:${e.key}:annex`, `${l} 별관`, e.annex);
  }
  for (const [k, v] of Object.entries(m.overrides)) put(`o:${k}`, `${calcLabel(m, k)} 수동 입력`, v);
  return out;
}

const HISTORY_VALUE_MAX = 40;

/** 긴 값은 처음 달라진 곳 둘레만 이력에 남긴다(앞부분이 같아도 차이가 보이도록) */
function historyValues(from: string, to: string): [string, string] {
  if (from.length <= HISTORY_VALUE_MAX && to.length <= HISTORY_VALUE_MAX) return [from, to];
  let diff = 0;
  while (diff < from.length && from[diff] === to[diff]) diff++;
  const start = Math.max(0, diff - 10), end = start + HISTORY_VALUE_MAX;
  const cut = (v: string) => `${start > 0 ? '…' : ''}${v.slice(start, end)}${v.length > end ? '…' : ''}`;
  return [cut(from), cut(to)];
}

/** 저장 전후 비교. 추가·삭제된 칸은 비어 있던 값과의 차이로 남긴다. */
export function minutesChanges(prev: MeetingMinutes, next: MeetingMinutes, at: string, by: string): MinutesChange[] {
  const a = flatten(prev), b = flatten(next);
  const out: MinutesChange[] = [];
  for (const key of new Set([...a.keys(), ...b.keys()])) {
    const from = a.get(key)?.value ?? '', to = b.get(key)?.value ?? '';
    if (from !== to) {
      const [shownFrom, shownTo] = historyValues(from, to);
      out.push({ at, by, label: (b.get(key) ?? a.get(key))!.label, from: shownFrom, to: shownTo });
    }
  }
  return out;
}

/* ---------------- 월 마감 체크 ---------------- */

export const CLOSE_CHECKS = [
  ['ATTENDANCE', '출결·취소 내역 대조'],
  ['PAYMENTS', '수납·환불 차이 확인'],
  ['PAYOUT_BASIS', '계약별 지급 기준 검토'],
  ['FINAL_REVIEW', '담당자 최종 검토'],
] as const;
export type CloseCheck = (typeof CLOSE_CHECKS)[number][0];

/** 센터·월별 결산 기록(회의록 + 마감 체크) */
export interface ClosingBook {
  centerId: string;
  month: string;
  checks: CloseCheck[];
  minutes: MeetingMinutes | null;
  /** 회의록 변경 이력(오래된 순, 최근 MINUTES_HISTORY_MAX 건) */
  history?: MinutesChange[];
  version: number;
  updatedAt: string;
  updatedBy: string;
}

export function normalizeChecks(v: unknown): CloseCheck[] {
  const keys = CLOSE_CHECKS.map(([k]) => k) as CloseCheck[];
  return Array.isArray(v) ? keys.filter((k) => v.includes(k)) : [];
}

/* ---------------- 결산 계산 ---------------- */

export interface BillLike {
  required: number;
  paid: { total: number };
}

/** 수업 청구액·수납액·미수금(= 청구 − 수납, 초과수납이면 음수) */
export function settlementTotals(bills: readonly BillLike[]) {
  const billed = sum(bills.map((b) => b.required));
  const collected = sum(bills.map((b) => b.paid.total));
  return { billed, collected, outstanding: billed - collected };
}

/** 배분율(%) 0~100, 소수 1자리까지 */
export function clampRate(v: unknown): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(Math.min(100, Math.max(0, n)) * 10) / 10;
}

/** 사업소득 지급 예정액(세전) = 수납 기준액 × 배분율. 1원 미만은 버린다. */
export const payoutOf = (basis: number, ratePct: number) => Math.floor((Math.max(0, basis) * clampRate(ratePct)) / 100);

/**
 * 담당자별 수납 기준액: 이용자별 수납액을 그 달 담당 회기 수 비율로 나눈다(나머지 원은 회기가 가장 많은 담당자에게).
 * sessions 는 취소 제외 회기.
 */
export function payoutBasis(
  bills: ReadonlyArray<{ clientId: string; paid: { total: number } }>,
  sessions: ReadonlyArray<{ clientId: string; therapistId: string }>,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const b of bills) {
    const counts = new Map<string, number>();
    for (const s of sessions) if (s.clientId === b.clientId) counts.set(s.therapistId, (counts.get(s.therapistId) ?? 0) + 1);
    const total = sum([...counts.values()]);
    if (!total || b.paid.total <= 0) continue;
    const entries = [...counts.entries()].sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]));
    const shares = entries.map(([, c]) => Math.floor((b.paid.total * c) / total));
    // 버림으로 생긴 나머지 원은 회기가 가장 많은 담당자에게(합계가 수납액과 정확히 같게)
    shares[0]! += b.paid.total - sum(shares);
    entries.forEach(([id], i) => out.set(id, (out.get(id) ?? 0) + shares[i]!));
  }
  return out;
}

/** 급여명세서 미리보기 계산: 지급 합계 = 기본급 + 수당, 차인지급액 = 지급 합계 − 공제 합계 */
export function payslip(i: { base: number; allowances: readonly number[]; deductions: readonly number[] }) {
  const gross = cellWon(i.base) + sum(i.allowances.map(cellWon));
  const deduction = sum(i.deductions.map(cellWon));
  return { gross, deduction, net: gross - deduction };
}
